/**
 * Leitura do codec de áudio de um MP4 no navegador.
 *
 * `MediaRecorder.isTypeSupported('audio/mp4')` responder `true` NÃO garante AAC.
 * O Chrome, em máquina sem encoder AAC do sistema, aceita `audio/mp4` e entrega
 * um MP4 com faixa **Opus** dentro: `blob.type` continua `audio/mp4`, o box
 * `ftyp` continua válido, e todo teste de container passa. A Meta repassa o
 * arquivo sem transcodificar; o player nativo do Instagram/WhatsApp no celular
 * não decodifica Opus em MP4 e o áudio toca MUDO (no desktop toca, porque quem
 * decodifica é o próprio Chrome).
 *
 * Por isso o formato é conferido pelos BYTES antes de enviar, e não pelo
 * mimetype declarado.
 */

/** Sample entry de AAC em MP4 — o único que os apps da Meta reproduzem. */
export const SAMPLE_ENTRY_AAC = 'mp4a';

/**
 * Bytes suficientes para alcançar o `stsd`. Ele vive dentro do `moov`, que em
 * MP4 fragmentado (o que o MediaRecorder produz) vem logo depois do `ftyp`;
 * com `+faststart` no progressivo, idem. 256 KB cobrem os dois casos com folga
 * sem ler o arquivo inteiro.
 */
const BYTES_DE_CABECALHO = 256 * 1024;

const ascii = (bytes: Uint8Array, inicio: number, fim: number): string =>
    String.fromCharCode(...bytes.subarray(inicio, fim));

/** Índice do literal `stsd` nos bytes, ou -1. */
function indiceDoStsd(bytes: Uint8Array): number {
    const [s, t, d1, d2] = [0x73, 0x74, 0x73, 0x64];
    for (let i = 0; i + 3 < bytes.length; i++) {
        if (bytes[i] === s && bytes[i + 1] === t && bytes[i + 2] === d1 && bytes[i + 3] === d2) return i;
    }
    return -1;
}

/**
 * Fourcc do primeiro sample entry do `stsd` (`mp4a`, `Opus`, `alac`…), ou `null`
 * quando o box não está no cabeçalho lido.
 *
 * Layout a partir do literal: `stsd`(4) version+flags(4) entryCount(4)
 * entrySize(4) fourcc(4) — daí o deslocamento de 16 bytes. Não é um parser de
 * MP4: é o mínimo para saber o codec.
 */
export async function codecDeAudioMp4(blob: Blob): Promise<string | null> {
    const buffer = await blob.slice(0, BYTES_DE_CABECALHO).arrayBuffer();
    const bytes = new Uint8Array(buffer);
    if (bytes.length < 12 || ascii(bytes, 4, 8) !== 'ftyp') return null;
    const i = indiceDoStsd(bytes);
    if (i < 0 || bytes.length < i + 20) return null;
    return ascii(bytes, i + 16, i + 20);
}

/** `true` quando o Blob é um MP4 com faixa AAC (`mp4a`). */
export async function ehAudioMp4Aac(blob: Blob): Promise<boolean> {
    return (await codecDeAudioMp4(blob)) === SAMPLE_ENTRY_AAC;
}
